// src/services/inviteService.js
import { db, app } from '@/firebase/firebase';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  orderBy,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { normalizeTeamRole, TEAM_ROLES } from '@/constants/roles';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';

function toMillis(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const t = new Date(ts).getTime();
  return Number.isFinite(t) ? t : null;
}

function generateInviteCode() {
  // 8 character uppercase code
  return Math.random().toString(36).slice(2, 10).toUpperCase();
}

export function buildInviteUrl(code, teamId) {
  if (!code || !teamId) {
    console.warn('[buildInviteUrl] code and teamId are required');
    return '';
  }
  const baseUrl = (import.meta.env.VITE_APP_BASE_URL || window.location.origin).replace(/\/+$/, '');
  return `${baseUrl}/invite/${code}?t=${teamId}`;
}

/**
 * Server-side delivery of an invite via the deliverInvite cloud
 * function. Used by:
 *   - The roster's "Invite all parents" bulk action
 *   - The per-player roster send icon (when the player has an email
 *     and no existing invite — one-click flow)
 *   - The InviteParentForPlayerModal "Re-send email" button (re-uses
 *     the existing invite link, doesn't create a duplicate)
 *
 * Args:
 *   method    — 'email' or 'sms'
 *   to        — recipient address / phone
 *   inviteUrl — full invite URL (use buildInviteUrl)
 *   code      — (optional) invite code so the server can fetch the
 *               invite doc directly instead of parsing it back out
 *               of the URL. Improves robustness if URL formats change.
 *
 * Returns: { ok: true } on success, throws on failure. Caller is
 * responsible for catching and surfacing errors via toast.
 */
export async function sendInviteDelivery({ method, to, inviteUrl, code, language }) {
  if (!method) throw new Error('sendInviteDelivery: method is required');
  if (!to) throw new Error('sendInviteDelivery: recipient (to) is required');
  if (!inviteUrl) throw new Error('sendInviteDelivery: inviteUrl is required');

  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'scrimmage-connect';
  const url = `https://us-central1-${projectId}.cloudfunctions.net/deliverInvite`;

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `language` lets a re-send override the language stored on an older
    // invite doc (created before bilingual emails), so re-sending to a
    // Spanish-speaking family lands in Spanish.
    body: JSON.stringify({ method, to, inviteUrl, code: code || null, language: language || null }),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`deliverInvite ${res.status}: ${txt || 'failed'}`);
  }
  return { ok: true };
}

/* --------------------------------------------------------------------------------------------------
   CREATE INVITE - UPDATED TO SAVE NAME/LABEL/METADATA
-------------------------------------------------------------------------------------------------- */
export async function createInvite(inviteData) {
  const {
    teamId,
    teamName,
    audience = 'staff',
    preset = TEAM_ROLES.STAFF,
    createdBy,
    email,
    phone,
    expiresInDays = 14,
    // ✅ ADDED: Capture these new fields
    name,
    label,
    metadata,
    // Recipient language ('en' | 'es') — stamped so deliverInvite can
    // localize the email/SMS to the family's language. Defaults to 'en'.
    language,
  } = inviteData || {};

  if (!teamId) throw new Error('teamId is required');
  if (!createdBy) throw new Error('createdBy is required');

  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to create an invite.');

  const code = generateInviteCode();
  const now = serverTimestamp();

  let expiresAt = null;
  if (expiresInDays && Number(expiresInDays) > 0) {
    const millis = Date.now() + Number(expiresInDays) * 24 * 60 * 60 * 1000;
    expiresAt = Timestamp.fromMillis(millis);
  }

  const inviteRef = doc(db, 'invites', code);

  const domain = typeof email === 'string' && email.includes('@')
    ? email.split('@')[1]
    : null;

  // consolidate name/label logic
  const savedLabel = label || name || null;

  // Normalize-on-write: for staff audiences, collapse any legacy preset
  // (`manager` / `coach` / `assistant` / `team_manager`) onto the canonical
  // `staff` role so downstream readers don't need to re-normalize. Other
  // audiences (player/parent/etc.) keep their literal preset.
  const audienceLower = String(audience || 'staff').toLowerCase();
  const presetCanonical = normalizeTeamRole(preset);
  const roleToWrite = audienceLower === 'staff' && presetCanonical
    ? presetCanonical
    : preset;

  // Denormalize teamName onto the invite so unauthenticated landing pages
  // (Register/Login/AcceptInvite) can render "Join {team}" without needing
  // to read the auth-gated team doc. Caller can pass it directly; otherwise
  // fetch it here from the team doc the inviter already has access to.
  let resolvedTeamName = teamName || null;
  if (!resolvedTeamName) {
    try {
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      if (teamSnap.exists()) {
        const td = teamSnap.data() || {};
        resolvedTeamName = td.name || td.teamName || null;
      }
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.warn('[createInvite] failed to read team for teamName', err);
      }
    }
  }

  await setDoc(inviteRef, {
    teamId,
    teamName: resolvedTeamName,
    audience,                   // "staff"
    preset: roleToWrite,        // canonical for staff audiences
    role: roleToWrite,
    kind: roleToWrite,
    status: 'active',

    code,
    createdAt: now,
    createdBy,
    updatedAt: now,

    email: email || null,
    phone: phone || null,
    emailDomain: domain || null,

    // ✅ ADDED: Write the new fields to Firestore
    name: savedLabel,
    label: savedLabel,
    metadata: metadata || {},
    language: String(language || '').toLowerCase().startsWith('es') ? 'es' : 'en',

    expiresAt,
    maxUses: 1,
    acceptedCount: 0,
    usedCount: 0,
  });

  track(ACTIVATION_EVENTS.STAFF_INVITED, {
    team_id: teamId,
    audience: audienceLower,
    role: roleToWrite,
    delivery: email ? 'email' : phone ? 'phone' : 'link',
  });

  return { id: inviteRef.id, code };
}

export async function getInviteByCode(code) {
  if (!code) throw new Error('code is required');
  const directRef = doc(db, 'invites', code);
  const directSnap = await getDoc(directRef);
  if (directSnap.exists()) return { id: directSnap.id, ...directSnap.data() };
  const q = query(collection(db, 'invites'), where('code', '==', code), limit(1));
  const qs = await getDocs(q);
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}

export async function listTeamInvites(teamId) {
  if (!teamId) throw new Error('teamId is required');
  const q = query(
    collection(db, 'invites'),
    where('teamId', '==', teamId),
    orderBy('createdAt', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/* --------------------------------------------------------------------------------------------------
   FIRESTORE-ONLY REVOKE
-------------------------------------------------------------------------------------------------- */
export async function revokeInvite(code) {
  if (!code) throw new Error('code is required');

  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in.');

  // Try direct ID first
  let inviteRef = doc(db, 'invites', code);
  let snap = await getDoc(inviteRef);

  // If not found, lookup by code field
  if (!snap.exists()) {
    const q = query(collection(db, 'invites'), where('code', '==', code), limit(1));
    const qs = await getDocs(q);
    if (qs.empty) {
      throw new Error('Invite not found.');
    }
    inviteRef = qs.docs[0].ref;
    snap = qs.docs[0];
  }

  const data = snap.data() || {};
  const status = String(data.status || 'active').toLowerCase();

  // Already revoked? Return success anyway.
  if (status === 'revoked') {
    return { ok: true, alreadyRevoked: true };
  }

  await updateDoc(inviteRef, {
    status: 'revoked',
    revokedAt: serverTimestamp(),
    revokedBy: currentUser.uid,
    updatedAt: serverTimestamp(),
  });

  return { ok: true, id: inviteRef.id };
}

/* --------------------------------------------------------------------------------------------------
   REACTIVATE / CLONE INVITE - UPDATED TO PRESERVE METADATA
-------------------------------------------------------------------------------------------------- */
export async function reactivateByClone({ teamId, inviteId, createdBy }) {
  if (!inviteId) throw new Error('inviteId is required');

  const existingSnap = await getDoc(doc(db, 'invites', inviteId));
  const existing = existingSnap.exists() ? existingSnap.data() : {};

  return createInvite({
    teamId: teamId || existing.teamId,
    teamName: existing.teamName || null,
    audience: existing.audience || 'staff',
    preset: existing.preset || existing.role || existing.kind || TEAM_ROLES.STAFF,
    createdBy,
    email: existing.email || '',
    phone: existing.phone || '',
    expiresInDays: 14,
    // ✅ ADDED: Carry over the name/metadata from the old invite
    name: existing.name || existing.label,
    label: existing.label || existing.name,
    metadata: existing.metadata || {},
  });
}

/* --------------------------------------------------------------------------------------------------
   MAIL / SMS HELPERS
-------------------------------------------------------------------------------------------------- */
export function mailtoForInvite({ link, to = '' }) {
  const subject = encodeURIComponent("You're invited to join my team on Scrimly");
  const body = encodeURIComponent(`Hi,\n\nPlease use the link below to join as a manager:\n\n${link}\n\nThanks!`);
  return `mailto:${to}?subject=${subject}&body=${body}`;
}

export function smsForInvite({ link, to = '' }) {
  const body = encodeURIComponent(`Join my team on Scrimly with this link: ${link}`);
  return `sms:${to}?&body=${body}`;
}

/* --------------------------------------------------------------------------------------------------
   ACCEPT INVITE
-------------------------------------------------------------------------------------------------- */
export async function acceptInvite({ code }) {
  if (!code) throw new Error('code is required');

  const functions = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable(functions, 'claimInvite');

  try {
    const { data } = await fn({ code });
    return data;

  } catch (err) {
    console.error('--- ERROR WHEN CALLING BACKEND ---', err);

    const msg = err?.message || '';
    const codeName = err?.code || '';
    const functionMissing =
      codeName === 'functions/not-found' ||
      /function.*not.*found|UNIMPLEMENTED/i.test(msg);

    if (functionMissing) {
      const auth = getAuth(app);
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not signed in for client-side fallback.');

      return await acceptInviteTransaction({ code, userId });
    }

    throw err;
  }
}

export async function claimInvite({ code, relation = null, playerProfileId = null }) {
  if (!code) throw new Error('code is required');
  const functions = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable(functions, 'claimInvite');
  const { data } = await fn({ code, relation, playerProfileId });
  return data;
}

/* --------------------------------------------------------------------------------------------------
   TRANSACTION-BASED ACCEPT
-------------------------------------------------------------------------------------------------- */
export async function acceptInviteTransaction({ code, userId }) {
  if (!code || !userId) throw new Error('code and userId are required');

  let inviteRef = doc(db, 'invites', code);
  let inviteDoc = await getDoc(inviteRef);

  if (!inviteDoc.exists()) {
    const q = query(collection(db, 'invites'), where('code', '==', code), limit(1));
    const qs = await getDocs(q);
    if (qs.empty) throw new Error('Invite not found.');
    inviteDoc = qs.docs[0];
    inviteRef = inviteDoc.ref;
  }

  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(inviteRef);
    if (!snap.exists()) throw new Error('Invite not found.');

    const inv = snap.data();
    const now = serverTimestamp();

    // Expired?
    const expMs = toMillis(inv.expiresAt);
    if (expMs && expMs <= Date.now()) {
      tx.update(inviteRef, { status: 'expired', updatedAt: now });
      throw new Error('This invite has expired.');
    }

    // Usage limits
    const maxUses = Number.isFinite(inv.maxUses) ? inv.maxUses : 1;
    const usedCount = Number(inv.usedCount || 0);
    if (usedCount >= maxUses) {
      tx.update(inviteRef, { status: 'expired', updatedAt: now });
      throw new Error('This invite has already been used.');
    }

    // Determine role.
    //
    // Legacy invite docs may carry `role: 'manager' | 'coach' | 'assistant' |
    // 'team_manager'`. For staff audiences we collapse any of those onto the
    // canonical TEAM_ROLES.STAFF so the member doc we write + the staffIds
    // allowlist stay consistent with teamService.js. Player/parent/other
    // audiences keep their literal role.
    const audience = String(inv.audience || 'staff').toLowerCase();
    const rawRole =
      String(inv.role || inv.kind || (audience === 'staff' ? TEAM_ROLES.STAFF : audience)).toLowerCase();
    const canonicalTeamRole = normalizeTeamRole(rawRole); // 'owner' | 'staff' | null
    const role = canonicalTeamRole ?? rawRole;

    if (!inv.teamId) throw new Error('Invite missing teamId.');

    // Team member path
    const memberRef = doc(db, 'teams', inv.teamId, 'members', userId);
    const memberSnap = await tx.get(memberRef);

    if (!memberSnap.exists()) {
      tx.set(memberRef, {
        uid: userId,
        role,
        addedVia: 'invite',
        inviteCode: inv.code || code,
        createdAt: now,
      });

      const teamRef = doc(db, 'teams', inv.teamId);

      const teamUpdates = { memberIds: arrayUnion(userId), updatedAt: now };
      // Only staff-tier roles (owner/staff, plus legacy spellings that fold
      // onto them via normalizeTeamRole) get mirrored to staffIds[].
      if (canonicalTeamRole) teamUpdates.staffIds = arrayUnion(userId);
      if (role === 'player') teamUpdates.playerIds = arrayUnion(userId);
      if (role === 'parent') teamUpdates.parentIds = arrayUnion(userId);

      tx.set(teamRef, teamUpdates, { merge: true });
    }

    const nextUsed = usedCount + 1;
    const singleUse = (maxUses || 1) <= 1 || audience === 'staff';

    const inviteUpdates = {
      usedCount: increment(1),
      acceptedCount: increment(1),
      lastAcceptedBy: userId,
      lastAcceptedAt: now,
      updatedAt: now,
    };

    if (singleUse) {
      inviteUpdates.status = 'accepted';
      inviteUpdates.acceptedBy = userId;
      inviteUpdates.acceptedAt = now;
    } else if (nextUsed >= maxUses) {
      inviteUpdates.status = 'expired';
    }

    tx.update(inviteRef, inviteUpdates);

    return { teamId: inv.teamId, role, inviteId: inviteRef.id };
  });
}

/* --------------------------------------------------------------------------------------------------
   INTEREST FUNCTIONS
-------------------------------------------------------------------------------------------------- */
// (Keep remaining functions exactly as they were...)

export async function acceptInterest(interestId, scrimmageId) {
  const ACCEPT_INTEREST_URL = import.meta.env.VITE_ACCEPT_INTEREST_URL;
  if (!ACCEPT_INTEREST_URL) throw new Error('VITE_ACCEPT_INTEREST_URL is not configured.');
  if (!interestId || !scrimmageId) throw new Error('interestId and scrimmageId are required.');
  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to perform this action.');
  const token = await currentUser.getIdToken();
  const res = await fetch(ACCEPT_INTEREST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data: { interestId, scrimmageId } }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || `Failed to accept interest (HTTP ${res.status})`);
  return body?.result || body;
}

export async function declineInterest(interestId) {
  const DECLINE_INTEREST_URL = import.meta.env.VITE_DECLINE_INTEREST_URL;
  if (!DECLINE_INTEREST_URL) throw new Error('VITE_DECLINE_INTEREST_URL is not configured.');
  if (!interestId) throw new Error('interestId is required.');
  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to perform this action.');
  const token = await currentUser.getIdToken();
  const res = await fetch(DECLINE_INTEREST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data: { interestId } }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || `Failed to decline interest (HTTP ${res.status})`);
  return body?.result || body;
}

export async function expressInterest(interestData) {
  const EXPRESS_INTEREST_URL = import.meta.env.VITE_EXPRESS_INTEREST_URL;
  if (!EXPRESS_INTEREST_URL) throw new Error('VITE_EXPRESS_INTEREST_URL is not configured.');
  if (!interestData?.scrimmageId || !interestData?.fromTeamId) throw new Error('scrimmageId and fromTeamId are required.');
  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to perform this action.');
  const token = await currentUser.getIdToken();
  const res = await fetch(EXPRESS_INTEREST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data: interestData }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || `Failed to express interest (HTTP ${res.status})`);
  return body?.result || body;
}

export async function hasExpressedInterest(scrimmageId, fromTeamId) {
  if (!scrimmageId || !fromTeamId) {
    console.warn('[hasExpressedInterest] scrimmageId and fromTeamId are required.');
    return false;
  }
  const interestsRef = collection(db, 'interests');
  const q = query(
    interestsRef,
    where('scrimmageId', '==', scrimmageId),
    where('fromTeamId', '==', fromTeamId),
    limit(1)
  );
  try {
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking for interest:', error);
    return false;
  }
}

export async function withdrawInterest(interestId) {
  const WITHDRAW_INTEREST_URL = import.meta.env.VITE_WITHDRAW_INTEREST_URL;
  if (!WITHDRAW_INTEREST_URL) throw new Error('VITE_WITHDRAW_INTEREST_URL is not configured.');
  if (!interestId) throw new Error('interestId is required.');
  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to perform this action.');
  const token = await currentUser.getIdToken();
  const res = await fetch(WITHDRAW_INTEREST_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data: { interestId } }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || `Failed to withdraw interest (HTTP ${res.status})`);
  return body?.result || body;
}